import { useEffect, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs } from '@/components/ui/tabs'
import { TabGroup } from '@/components/ui/tab-group'
import { NodeAvatar, NodePropertyString } from '@/components/nodes'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { UserStatusDisplay } from '@/components/users'
import { userService } from '@/services'
import { useAuth } from '@/hooks/useAuth'
import type { User } from '@/types'
import { UserStatus, UserRole } from '@/types/enums'
import { Plus } from 'lucide-react'
import { usersPageTabs } from '@/lib/config/tabs'
import {
  PageContainer,
  PageHeader,
  ListPageLayout,
  LoadingState,
  EmptyState,
} from '@/components/layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Mail, MapPin } from 'lucide-react'

type UserTabValue = 'all-users' | 'admins' | 'members' | 'online'

/**
 * Get badge variant based on role
 */
function getRoleBadgeVariant(role: string): 'default' | 'secondary' | 'destructive' {
  switch (role) {
    case 'SuperAdmin':
      return 'destructive'
    case 'Admin':
      return 'default'
    case 'Member':
      return 'secondary'
    default:
      return 'secondary'
  }
}

export default function UsersPage() {
  const { user } = useAuth()
  const [users, setUsers] = useState<User[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [activeTab, setActiveTab] = useState<UserTabValue>('all-users')

  const loadUsers = useCallback(async () => {
    if (!user) return

    try {
      setLoading(true)
      const allUsers = await userService.getAll()

      // SuperAdmins see all users, others see users in their workspaces
      if (user.role === UserRole.SuperAdmin) {
        setUsers(allUsers)
      } else {
        // Filter users who share at least one workspace with the current user
        const userWorkspaceIds = user.workspaces.map((w) => w.id)
        const workspaceUsers = allUsers.filter((u) =>
          u.workspaces.some((w) => userWorkspaceIds.includes(w.id))
        )
        setUsers(workspaceUsers)
      }
    } catch (error) {
      console.error('Failed to load users:', error)
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    loadUsers()
  }, [loadUsers])

  // Apply tab-based filtering first
  const tabFilteredUsers = users.filter((u) => {
    switch (activeTab) {
      case 'all-users':
        return true
      case 'admins':
        return u.role === UserRole.Admin || u.role === UserRole.SuperAdmin
      case 'members':
        return u.role === UserRole.Member
      case 'online':
        return (
          u.status === UserStatus.Online ||
          u.status === UserStatus.OnlineReadyToChat ||
          u.status === UserStatus.OnlineInDeepWork
        )
      default:
        return true
    }
  })

  // Get unique roles and statuses for filter dropdowns (from tab-filtered users)
  const uniqueRoles = Array.from(new Set(tabFilteredUsers.map((u) => u.role)))
  const uniqueStatuses = Array.from(new Set(tabFilteredUsers.map((u) => u.status)))

  const filteredUsers = tabFilteredUsers.filter((u) => {
    // Search filter
    const matchesSearch =
      u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.location?.toLowerCase().includes(searchQuery.toLowerCase())

    // Role filter
    const matchesRole = roleFilter === 'all' || u.role === roleFilter

    // Status filter
    const matchesStatus = statusFilter === 'all' || u.status === statusFilter

    return matchesSearch && matchesRole && matchesStatus
  })

  if (loading) {
    return (
      <PageContainer maxWidth="full">
        <LoadingState variant="skeleton" skeletonCount={6} />
      </PageContainer>
    )
  }

  return (
    <PageContainer maxWidth="full">
      <PageHeader
        title="Users"
        description="View and manage users across workspaces"
        action={
          user?.role === UserRole.SuperAdmin
            ? {
                label: 'Create User',
                href: '/users/new',
                icon: Plus,
              }
            : undefined
        }
      />

      <ListPageLayout
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        searchPlaceholder="Search users by name, email, or location"
        tabs={
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as UserTabValue)}>
            <TabGroup
              tabs={usersPageTabs}
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as UserTabValue)}
            />
          </Tabs>
        }
        actions={
          <div className="flex items-center gap-4">
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {uniqueRoles.map((role) => (
                  <SelectItem key={role} value={role}>
                    {role}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {uniqueStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {(roleFilter !== 'all' || statusFilter !== 'all') && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setRoleFilter('all')
                  setStatusFilter('all')
                }}
              >
                Clear Filters
              </Button>
            )}
          </div>
        }
      >
        {filteredUsers.length === 0 ? (
          <EmptyState
            title={
              searchQuery || roleFilter !== 'all' || statusFilter !== 'all'
                ? 'No users found'
                : 'No users in this view'
            }
            description={
              searchQuery || roleFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search criteria or filters'
                : 'No users to display'
            }
          />
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filteredUsers.map((u) => (
              <Link key={u.id} to={`/users/${u.id}`}>
                <Card className="hover:shadow-lg transition-shadow cursor-pointer h-full">
                  <CardContent className="pt-6">
                    <div className="flex items-start gap-4">
                      <NodeAvatar node={u} size="xl" />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-lg truncate">{u.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge
                            variant={getRoleBadgeVariant(u.role)}
                            className="text-xs px-1.5 py-0.5"
                          >
                            {u.role}
                          </Badge>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 space-y-2 text-sm">
                      <NodePropertyString
                        description={u.email}
                        icon={Mail}
                        textSize="xs"
                        className="text-muted-foreground"
                      />
                      <NodePropertyString
                        description={u.location}
                        icon={MapPin}
                        textSize="xs"
                        className="text-muted-foreground"
                      />
                      <UserStatusDisplay user={u} variant="dot-text" size="sm" format="short" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </ListPageLayout>
    </PageContainer>
  )
}
